// composables/useApi.js
import {useAuthStore} from "~/stores/auth/index.js";

export const useApi = () => {
    const config = useRuntimeConfig()
    const API_BASE_URL = config.public.apiBase

    // Flag pour éviter les appels de refresh simultanés
    let isRefreshing = false
    let failedQueue = []

    // Fonction pour traiter la queue des requêtes en attente
    const processQueue = (error, success = null) => {
        failedQueue.forEach(({ resolve, reject }) => {
            if (error) {
                reject(error)
            } else {
                resolve(success)
            }
        })

        failedQueue = []
    }

    // $fetch de Nuxt avec gestion automatique du refresh via cookies
    const apiFetch = $fetch.create({
        baseURL: API_BASE_URL + '/v1',
        credentials: 'include', // Important pour envoyer les cookies

        // Intercepteur pour gérer les erreurs d'authentification
        async onResponseError({ request, response, options }) {
            const originalRequest = { request, options }

            // Si erreur 401 et ce n'est pas déjà une requête de refresh/login
            if (response.status === 401 &&
                !request.toString().includes('/auth/refresh') &&
                !request.toString().includes('/auth/login')) {

                // Si on est déjà en train de refresh
                if (isRefreshing) {
                    // Ajouter la requête à la queue
                    return new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject })
                    }).then(() => {
                        // Réessayer la requête originale (les cookies seront mis à jour automatiquement)
                        return $fetch(originalRequest.request, originalRequest.options)
                    })
                }

                isRefreshing = true

                try {
                    // Tenter le refresh du token (le nouveau token sera automatiquement dans le cookie)
                    await $fetch('/auth/refresh-token', {
                        method: 'POST',
                        baseURL: API_BASE_URL + '/v1',
                        credentials: 'include'
                    })

                    console.log('Token refreshed automatically via cookie')

                    // Refresh réussi, traiter la queue
                    processQueue(null, true)

                    // Réessayer la requête originale
                    return $fetch(originalRequest.request, originalRequest.options)

                } catch (refreshError) {
                    // Refresh échoué, déconnecter l'utilisateur
                    console.log('Cookie refresh failed, logging out')
                    processQueue(refreshError, null)

                    // const authStore = useAuthStore()
                    // await authStore.logout()

                    // Rediriger vers la page de login
                    // await navigateTo('/auth/login')

                    throw refreshError
                } finally {
                    isRefreshing = false
                }
            }

            // Pour les autres erreurs, laisser passer
            throw response._data || response
        }
    })

    // Wrapper pour gérer les erreurs de manière cohérente
    const handleApiCall = async (apiCall) => {
        try {
            return await apiCall()
        } catch (error) {
            // Gestion des erreurs comme avant

            // Déterminer les données d'erreur (peut être error.response.data ou error.data)
            const errorData = error.response?.data || (error.data || error)
            const status = error.response?.status || error.status || error.statusCode
            console.log('Error structure:', errorData, status) // Pour débugger

            if (errorData) {
                let errorMessage = errorData.message || errorData.error || error.message

                // Gestion spécifique des erreurs de validation (400)
                if (errorData.details && Array.isArray(errorData.details)) {
                    // Transformer les détails de validation en message lisible
                    const validationMessages = errorData.details.map(detail => detail.message)
                    errorMessage = validationMessages.join(', ')
                }

                const enhancedError = new Error(errorMessage)
                enhancedError.response = {
                    status: status,
                    data: errorData
                }

                throw enhancedError
            }

            const enhancedError = new Error(error.message || 'Network error')
            enhancedError.response = {
                status: status || 500,
                data: {
                    error: error.message || 'Network error',
                    message: error.message || 'Network error'
                }
            }
            throw enhancedError
        }
    }

    // === AUTH ===
    const login = (credentials) =>
        handleApiCall(() => apiFetch('/auth/login', { method: 'POST', body: credentials }))

    const register = (userData) =>
        handleApiCall(() => apiFetch('/auth/register', { method: 'POST', body: userData }))

    const logout = () =>
        handleApiCall(() => apiFetch('/auth/logout', { method: 'POST' }))

    const logoutAll = () =>
        handleApiCall(() => apiFetch('/auth/logout-all', { method: 'POST' }))

    const getProfile = () =>
        handleApiCall(() => apiFetch('/auth/me', { method: 'GET' }))

    const refreshToken = () =>
        handleApiCall(() => apiFetch('/auth/refresh', { method: 'POST' }))

    const verifyToken = () =>
        handleApiCall(() => apiFetch('/auth/verify', { method: 'POST' }))

    const forgotPassword = (email) =>
        handleApiCall(() => apiFetch('/auth/forgot-password', { method: 'POST', body: { email } }))

    const resetPassword = (resetData) =>
        handleApiCall(() => apiFetch('/auth/reset-password', { method: 'POST', body: resetData }))

    const changePassword = (userId,passwordData) =>
        handleApiCall(() => apiFetch(`/user/${userId}/change-password`, { method: 'PUT', body: passwordData }))
    const changePassword_admin = (userId,passwordData) =>
        handleApiCall(() => apiFetch(`/user/${userId}/change-password/admin`, { method: 'PUT', body: passwordData }))

    const getSessions = () =>
        handleApiCall(() => apiFetch('/auth/sessions', { method: 'GET' }))

    const deleteSession = (sessionId) =>
        handleApiCall(() => apiFetch(`/auth/sessions/${sessionId}`, { method: 'DELETE' }))

    const cleanExpiredSessions = () =>
        handleApiCall(() => apiFetch('/auth/clean-sessions', { method: 'POST' }))

    // === USERS ===
    const getUsers = (params = {}) =>
        handleApiCall(() => apiFetch('/user/get-all', { method: 'GET', params }))

    const createUser = (userData) =>
        handleApiCall(() => apiFetch('/user/create', { method: 'POST', body: userData }))

    const updateUser = (userId, userData) =>
        handleApiCall(() => apiFetch(`/user/${userId}`, { method: 'PUT', body: userData }))

    const deactivateUser = (userId, data = {}) =>
        handleApiCall(() => apiFetch(`/user/${userId}/deactivate`, { method: 'PUT', body: data }))

    const reactivateUser = (userId, data = {}) =>
        handleApiCall(() => apiFetch(`/user/${userId}/reactivate`, { method: 'PUT', body: data }))

    const resetUserPassword = (userId) =>
        handleApiCall(() => apiFetch(`/user/${userId}/reset-password`, { method: 'POST' }))

    const assignUserRole = (userId, roleId) =>
        handleApiCall(() => apiFetch(`/user/${userId}/assign-role`, {
            method: 'POST',
            body: { id: roleId }
        }))

    const getUsersStats = () =>
        handleApiCall(() => apiFetch('/user/stats/global', { method: 'GET' }))

    const bulkCreateUsers = (usersData) =>
        handleApiCall(() => apiFetch('/user/bulk-create', {
            method: 'POST',
            body: { users: usersData }
        }))


    const getUserProfile = (userId) =>
        handleApiCall(() => apiFetch(`/user/${userId}/profile`, { method: 'GET' }))

    // === ROLES ===
    const getRoles = (params = {}) =>
        handleApiCall(() => apiFetch('/role/get-all', { method: 'GET', params }))

    const createRole = (roleData) =>
        handleApiCall(() => apiFetch('/role/create', { method: 'POST', body: roleData }))

    const updateRole = (roleId, roleData) =>
        handleApiCall(() => apiFetch(`/role/update/${roleId}`, { method: 'PUT', body: roleData }))

    const getRoleById = (roleId) =>
        handleApiCall(() => apiFetch(`/role/get-by-id/${roleId}`, { method: 'GET' }))

    const getRoleByName = (roleName) =>
        handleApiCall(() => apiFetch(`/role/get-by-name//${roleName}`, { method: 'GET' }))

    const deactivateRole = (roleId, reason) =>
        handleApiCall(() => apiFetch(`/role/${roleId}/deactivate`, {
            method: 'PATCH',
            body: { ...(reason||{}) }
        }))

    const reactivateRole = (roleId, newExpirationDate, reason) =>
        handleApiCall(() => apiFetch(`/role/${roleId}/reactivate`, {
            method: 'PATCH',
            body: { expiresAt: newExpirationDate, reason }
        }))

    const extendRoleExpiration = (roleId, expiresAt, reason) =>
        handleApiCall(() => apiFetch(`/role/${roleId}/extend`, {
            method: 'PATCH',
            body: { expiresAt: expiresAt, reason }
        }))

    const getExpiredRoles = () =>
        handleApiCall(() => apiFetch('/role/expired', { method: 'GET' }))

    const autoDeactivateExpiredRoles = () =>
        handleApiCall(() => apiFetch('/role/auto-deactivate', { method: 'POST' }))

    const addPermissionToRole = (roleId, permissions) =>
        handleApiCall(() => apiFetch(`/role/${roleId}/add-permissions`, {
            method: 'POST',
            body: Array.isArray(permissions) ? permissions : [permissions]
        }))


    const checkRolePermission = (roleId, permission) =>
        handleApiCall(() => apiFetch(`/role/${roleId}/check-permission/${permission}`, {
            method: 'GET'
        }))

    const getRoleUsers = (roleId, params = {}) =>
        handleApiCall(() => apiFetch(`/role/get-users-belong-to/${roleId}`, {
            method: 'GET',
            params
        }))

    const getRolesStats = () =>
        handleApiCall(() => apiFetch('/role/stats/global', { method: 'GET' }))

    const duplicateRole = (roleId, newName, options = {}) =>
        handleApiCall(() => apiFetch(`/role/${roleId}/duplicate`, {
            method: 'POST',
            body: { newName, ...options }
        }))

    const getAvailablePermissions = () =>
        handleApiCall(() => apiFetch('/role/permissions/available', { method: 'GET' }))

    // === CLIENTS ===
    const getClients = (params = {}) =>
        handleApiCall(() => apiFetch('/client/get-all', { method: 'GET', params }))

    const createClient = (clientData) =>
        handleApiCall(() => apiFetch('/client/create', { method: 'POST', body: clientData }))

    const updateClient = (clientId, clientData) =>
        handleApiCall(() => apiFetch(`/client/${clientId}/update`, { method: 'PUT', body: clientData }))

    const getClientStats = (clientId) =>
        handleApiCall(() => apiFetch(`/client/${clientId}/stats`, { method: 'GET' }))

    // === PROJECTS ===
    const fetchProjects = (params = {}) =>
        handleApiCall(() => apiFetch('/project/get-all', { method: 'GET', params }))

    const getProjectById = (projectId) =>
        handleApiCall(() => apiFetch(`/project/${projectId}`, { method: 'GET' }))

    const createProject = (projectData) =>
        handleApiCall(() => apiFetch('/project/create', { method: 'POST', body: projectData }))

    const updateProject = (projectId, projectData) =>
        handleApiCall(() => apiFetch(`/project/${projectId}/update`, { method: 'PUT', body: projectData }))

    const getProjectStats = (projectId) =>
        handleApiCall(() => apiFetch(`/project/${projectId}/stats`, { method: 'GET' }))

    // === DOCUMENTS ===
    const getDocumentsByProject = (projectId, params = {}) =>
        handleApiCall(() => apiFetch(`/project-document/project/${projectId}`, { method: 'GET', params }))

    const uploadDocument = (projectId, formData) =>
        handleApiCall(() => apiFetch(`/project-document/upload/${projectId}`, { method: 'POST', body: formData }))

    const deleteDocument = (documentId) =>
        handleApiCall(() => apiFetch(`/project-document/${documentId}`, { method: 'DELETE' }))

    // === CONTACT REQUESTS ===
    const submitContactRequest = (contactData) =>
        handleApiCall(() => apiFetch('/contact-request/submit', {
            method: 'POST',
            body: contactData
        }))

    const addContactDocuments = (requestId, formData) =>
        handleApiCall(() => apiFetch(`/contact-request/${requestId}/documents`, {
            method: 'POST',
            body: formData
        }))

    const respondToContactRequest = (requestId, responseData) => {
        const isFormData = responseData instanceof FormData
        return handleApiCall(() => apiFetch(`/contact-request/requests/${requestId}/respond`, {
            method: 'POST',
            body: responseData,
            // Si c'est FormData, ne pas définir Content-Type (laissé automatiquement pour multipart/form-data)
            ...(isFormData ? {} : {
                headers: { 'Content-Type': 'application/json' }
            })
        }))
    }
    // Renvoyer email de confirmation client
    const resendClientConfirmation = (requestId) =>
        handleApiCall(() => apiFetch(`/contact-request/requests/${requestId}/resend-confirmation`, {
            method: 'POST'
        }))

    // Renvoyer email de réponse client
    const resendClientResponse = (responseId) =>
        handleApiCall(() => apiFetch(`/contact-request/responses/${responseId}/resend-email`, {
            method: 'POST'
        }))

    const updateContactRequestStatus = (requestId, statusData) =>
        handleApiCall(() => apiFetch(`/contact-request/requests/${requestId}/status`, {
            method: 'PUT',
            body: statusData
        }))

    const updateContactRequest = (requestId, updateData) =>
        handleApiCall(() => apiFetch(`/contact-request/requests/${requestId}`, {
            method: 'PUT',
            body: updateData
        }))

    // Mettre à jour le statut winProject
    const updateWinProject = (requestId, winProject, reason = null) =>
        handleApiCall(() => apiFetch(`/contact-request/requests/${requestId}/win-status`, {
            method: 'PUT',
            body: { winProject, reason }
        }))

    const getContactRequests = (params = {}) =>
        handleApiCall(() => apiFetch('/contact-request/requests', {
            method: 'GET',
            params
        }))

    const getContactRequestById = (requestId) =>
        handleApiCall(() => apiFetch(`/contact-request/requests/${requestId}`, {
            method: 'GET'
        }))



    const getContactRequestStats = (params = {}) =>
        handleApiCall(() => apiFetch('/contact-request/stats', {
            method: 'GET',
            params
        }))

    const downloadContactDocument = (documentId) =>
        handleApiCall(() => apiFetch(`/contact-request/documents/contact/${documentId}/download`, {
            method: 'GET',
            responseType: 'blob' // Pour le téléchargement de fichiers
        }))

    const downloadResponseDocument = (documentId) =>
        handleApiCall(() => apiFetch(`/contact-request/documents/response/${documentId}/download`, {
            method: 'GET',
            responseType: 'blob' // Pour le téléchargement de fichiers
        }))

    const previewContactDocument = (documentId) =>
        handleApiCall(() => apiFetch(`/contact-request/documents/contact/${documentId}/preview`, {
            method: 'GET'
        }))

    const previewResponseDocument = (documentId) =>
        handleApiCall(() => apiFetch(`/contact-request/documents/response/${documentId}/preview`, {
            method: 'GET'
        }))


    // Obtenir les statistiques du dashboard
    const getDashboardStats = (dateRange = 30) =>
        handleApiCall(() => apiFetch('/contact-request/dashboard/stats', {
            method: 'GET',
            params: { dateRange }
        }))

    // Obtenir les données du graphique groupées par mois
    const getRequestsGroupedByMonth = (year = new Date().getFullYear()) =>
        handleApiCall(() => apiFetch('/contact-request/dashboard/chart-data', {
            method: 'GET',
            params: { year }
        }))

    // Obtenir les demandes récentes
    const getRecentRequests = (limit = 3) =>
        handleApiCall(() => apiFetch('/contact-request/dashboard/recent-requests', {
            method: 'GET',
            params: { limit }
        }))

    // Obtenir les réponses récentes
    const getRecentResponses = (limit = 3) =>
        handleApiCall(() => apiFetch('/contact-request/dashboard/recent-responses', {
            method: 'GET',
            params: { limit }
        }))

    // Obtenir les documents d'une demande de contact
    const getContactDocuments = (requestId) =>
        handleApiCall(() => apiFetch(`/contact-request/requests/${requestId}/documents`, {
            method: 'GET'
        }))

    // Obtenir les documents d'une réponse
    const getResponseDocuments = (responseId) =>
        handleApiCall(() => apiFetch(`/contact-request/responses/${responseId}/documents`, {
            method: 'GET'
        }))

    // Supprimer un document de contact
    const removeContactDocument = (documentId) =>
        handleApiCall(() => apiFetch(`/contact-request/documents/contact/${documentId}`, {
            method: 'DELETE'
        }))

    // Supprimer un document de réponse
    const removeResponseDocument = (documentId) =>
        handleApiCall(() => apiFetch(`/contact-request/documents/response/${documentId}`, {
            method: 'DELETE'
        }))

    // === NOTIFICATIONS === (à ajouter dans votre useApi)
    const getNotifications = (params = {}) =>
        handleApiCall(() => apiFetch('/notifications', { method: 'GET', params }))

    const getNotificationCounts = (params = {}) =>
        handleApiCall(() => apiFetch('/notifications/counts', { method: 'GET', params }))

    const getNotificationStatus = (params = {}) =>
        handleApiCall(() => apiFetch('/notifications/status', { method: 'GET', params }))

    const markEntityAsSynced = (syncData) =>
        handleApiCall(() => apiFetch('/notifications/sync', { method: 'POST', body: syncData }))

    const testNotifications = () =>
        handleApiCall(() => apiFetch('/notifications/test', { method: 'GET' }))



    // Exposer apiFetch pour les cas spéciaux
    const directApiFetch = apiFetch
    return {
        // Auth
        login, register, logout, logoutAll, getProfile, refreshToken, verifyToken,
        forgotPassword, resetPassword, changePassword, getSessions, deleteSession, cleanExpiredSessions,
        changePassword_admin,
        // Users
        getUsers, createUser,  updateUser,
        deactivateUser, reactivateUser,
        resetUserPassword, assignUserRole,  getUsersStats, bulkCreateUsers,  getUserProfile,
        // Roles
        getRoles, createRole, updateRole,
        getRoleById,
        getRoleByName,
        deactivateRole,
        reactivateRole,
        extendRoleExpiration,
        getExpiredRoles,
        autoDeactivateExpiredRoles,
        addPermissionToRole,
        checkRolePermission,
        getRoleUsers,
        getRolesStats,
        duplicateRole,
        getAvailablePermissions,
        // Clients
        getClients, createClient, updateClient, getClientStats,
        // Projects
        fetchProjects, getProjectById, createProject, updateProject, getProjectStats,
        // Documents
        getDocumentsByProject, uploadDocument, deleteDocument,
        // contact request
        submitContactRequest,
        addContactDocuments,
        getContactRequests,
        getContactRequestById,
        respondToContactRequest,
        updateContactRequestStatus,
        updateContactRequest,
        getContactRequestStats,
        downloadContactDocument,
        downloadResponseDocument,
        previewContactDocument,
        previewResponseDocument,

        // === NOUVEAUX ENDPOINTS ===
        updateWinProject,
        getDashboardStats,
        getRequestsGroupedByMonth,
        getRecentRequests,
        getRecentResponses,
        getContactDocuments,
        getResponseDocuments,
        removeContactDocument,
        removeResponseDocument,
        resendClientConfirmation,
        resendClientResponse,

        // notification
        getNotifications,
        getNotificationCounts,
        getNotificationStatus,
        markEntityAsSynced,
        testNotifications,
        // Direct access to apiFetch for custom calls
        apiFetch: directApiFetch
    }
}